import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import {
  CompanyDoc,
  type Director,
  type Shareholder,
} from "components/Map/Sections/Compliance/Screening/types";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import { type IChecklistData } from "../components/Map/Sections/Compliance/Scoring/Checklist/types";
import { accountrtk } from "./api-accounts";
import type {
  AddCreditReportDocument,
  AddEDDDocument,
  CreditReportDocument,
  CreditReportResponse,
  EwalletItem,
  InternalBankAccount,
  MAP_TYPE,
  MapTemplate,
  ScoreObject,
  ScreeningStatus,
} from "./api-compliance.d";
import { maps } from "./api-maps";

export const compliance = createApi({
  reducerPath: "compliance",
  tagTypes: [
    "TEMPLATES",
    "SUMMARIES",
    "TABLE_VALUES",
    "SHAREHOLDERS",
    "DIRECTORS",
    "SCREENING",
    "CHECKLIST",
    "COMPLIANCE_SCORE",
    "PROVIDERS",
    "SCREENING_TOOLS",
    "CREDIT_REPORT_DOCUMENTS",
    "EDD_DOCUMENTS",
    "DOCUMENTS",
    "EWALLETS",
    "INTERNAL_BANK_ACCOUNTS",
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}`,
  }),
  endpoints: (builder) => ({
    getComplianceTemplates: builder.query<MapTemplate, MAP_TYPE>({
      query: (template_name) => ({
        url: `/maps/templates/compliance/${template_name}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["TEMPLATES"],
    }),
    getCreditReport: builder.query<
      CreditReportResponse,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/credit-report`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["TABLE_VALUES"],
    }),
    getCreditReportDocuments: builder.query<CreditReportDocument[], string>({
      query: (account_id) => ({
        url: `/compliance/${account_id}/credit-report-documents`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["CREDIT_REPORT_DOCUMENTS"],
    }),
    getEwalletsByAccount: builder.query<EwalletItem[], string>({
      query: (account_id) => ({
        url: `/compliance/account/${account_id}/ewallets`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["EWALLETS"],
    }),
    addEwalletByAccount: builder.mutation<
      EwalletItem[],
      { account_id: string; data: any }
    >({
      query: ({ account_id, data }) => ({
        url: `/compliance/account/${account_id}/ewallets`,
        method: "POST",
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["EWALLETS"],
    }),
    getInternalBankAccount: builder.query<InternalBankAccount[], void>({
      query: () => ({
        url: `/compliance/internal-bank-accounts`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["INTERNAL_BANK_ACCOUNTS"],
    }),
    editEwalletByAccount: builder.mutation<
      any,
      {
        account_id: string;
        ewallet_id: string;
        data: {
          currency: string;
          internal_bank_id: string;
          balance: any;
        };
      }
    >({
      query: ({ ewallet_id, account_id, data }) => {
        return {
          url: `/compliance/account/${account_id}/ewallet/${ewallet_id}`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["EWALLETS"],
    }),
    getEDDDocuments: builder.query<CompanyDoc[], string>({
      query: (account_id) => ({
        url: `/compliance/${account_id}/edd-documents`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["EDD_DOCUMENTS"],
    }),
    addCreditReportDocument: builder.mutation<
      any,
      { account_id: string; data: AddCreditReportDocument }
    >({
      query: ({ account_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("document_type", data.document_type);
        formData.append("document_category", data.document_category);
        return {
          url: `/accounts/${account_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["CREDIT_REPORT_DOCUMENTS", "TABLE_VALUES"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["TABLE_VALUES"]));
          dispatch(accountrtk.util.invalidateTags(["ACCOUNT_DOCS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error deleting director:", error);
        }
      },
    }),
    addEDDDocument: builder.mutation<
      any,
      { account_id: string; data: AddEDDDocument }
    >({
      query: ({ account_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("document_type", data.document_type);
        formData.append("document_category", data.document_category);
        return {
          url: `/accounts/${account_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["EDD_DOCUMENTS", "TABLE_VALUES"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["TABLE_VALUES"]));
          dispatch(accountrtk.util.invalidateTags(["ACCOUNT_DOCS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error deleting director:", error);
        }
      },
    }),
    getChecklist: builder.query<
      IChecklistData,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/checklist`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: (result, error, { map_name, account_type }) => [
        { type: "CHECKLIST", id: `${map_name}-${account_type}` },
      ],
      refetchOnMountOrArgChange: true,
    }),
    getComplianceScoring: builder.query<
      ScoreObject,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/checklist/compliance-risk-score`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["COMPLIANCE_SCORE"],
    }),
    getSummaries: builder.query<
      { deal: string; supplier: string; buyer: string },
      string
    >({
      query: (map_name) => ({
        url: `/maps/${map_name}/summaries`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SUMMARIES"],
    }),
    editSummaries: builder.mutation<
      any,
      {
        map_name: string;
        data: {
          summary: string;
          subject: string;
        };
      }
    >({
      query: ({ map_name, data }) => {
        return {
          url: `/maps/${map_name}/summaries`,
          method: "PUT",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SUMMARIES"],
      // async onQueryStarted(arg, { dispatch, queryFulfilled }) {
      //   try {
      //     await queryFulfilled;
      //     dispatch(compliance.util.invalidateTags(["TABLE_VALUES"]));
      //     dispatch(maps.util.invalidateTags(["TABLE_VALUES"]));
      //   } catch (error) {
      //     //eslint-disable-next-line no-console
      //     console.error("Error editing credit report:", error);
      //   }
      // },
    }),
    generateFactSheet: builder.query<any, string>({
      query: (map_name) => ({
        url: `/maps/${map_name}/investment-criteria-card/export`,
        method: "get",
      }),
      // transformResponse: (response) => response?.data,
      providesTags: [],
    }),
    editCreditReport: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        data: {
          map_metadata_id: string;
          source: string;
          value: any;
        };
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["TABLE_VALUES", "CHECKLIST", "COMPLIANCE_SCORE"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(compliance.util.invalidateTags(["TABLE_VALUES"]));
          dispatch(maps.util.invalidateTags(["TABLE_VALUES"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error editing credit report:", error);
        }
      },
    }),
    getScreening: builder.query<
      Record<string, any>,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/screening`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["TABLE_VALUES", "SCREENING"],
    }),
    getScreeningShareholders: builder.query<
      Shareholder[],
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/screening/shareholders`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SHAREHOLDERS"],
    }),
    getScreeningDirectors: builder.query<
      Director[],
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/screening/directors`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SHAREHOLDERS", "DIRECTORS"],
    }),
    uploadDocument: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        data: {
          file: File;
        };
        parentName: string;
      }
    >({
      query: ({ map_name, account_type, parentName, data }) => {
        const formData = new FormData();
        formData.append("file", data.file); // Append the file to FormData
        return {
          url: `/maps/${map_name}/${account_type}/${parentName}/risk/documents`,
          method: "POST",
          data: formData, // Corrected from 'data' to 'body'
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, error, { parentName }) =>
        result ? [{ type: "DOCUMENTS", id: parentName }] : [],
    }),

    getDocuments: builder.query<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        parentName: string;
      }
    >({
      query: ({ map_name, account_type, parentName }) => ({
        url: `/maps/${map_name}/${account_type}/${parentName}/risk/documents`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: (result, error, { parentName }) =>
        result ? [{ type: "DOCUMENTS", id: parentName }] : [],
    }),
    getSingleDocument: builder.query<
      any,
      {
        map_name: string;
        document_id: string;
      }
    >({
      query: ({ map_name, document_id }) => ({
        url: `/maps/${map_name}/risk/documents/${document_id}`, // Fixed missing '/'
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: (result, error, { document_id }) =>
        result ? [{ type: "DOCUMENTS", id: document_id }] : [],
    }),

    deleteDocument: builder.mutation<
      any,
      {
        map_name: string;
        document_id: string;
      }
    >({
      query: ({ map_name, document_id }) => ({
        url: `/maps/${map_name}/risk/documents/${document_id}`, // Fixed missing '/'
        method: "DELETE",
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, error, { document_id }) =>
        result ? [{ type: "DOCUMENTS", id: document_id }] : [],
    }),
    editScreeningCompany: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        data: {
          map_metadata_id: string;
          source: string;
          value: any;
        };
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SCREENING", "COMPLIANCE_SCORE", "CHECKLIST"],
    }),
    editCheckListCompany: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        data: {
          map_metadata_id: string;
          source: string;
          value: any;
        };
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, error, { map_name, account_type }) => [
        "SCREENING",
        { type: "CHECKLIST", id: `${map_name}-${account_type}` },
      ],
    }),
    editScreeningCompanyBatch: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        data: {
          map_metadata_id: string;
          source: string;
          value: any;
        }[];
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}/batch`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SCREENING", "COMPLIANCE_SCORE", "CHECKLIST"],
    }),
    editScreeningShareholder: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        shareholder_id: string;
        data: {
          status: string;
          remarks: string;
          source: string;
        };
      }
    >({
      query: ({ map_name, account_type, shareholder_id, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}/screening/shareholders/${shareholder_id}`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SHAREHOLDERS"],
    }),
    editScreeningShareholderBatch: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        data: {
          shareholder_id: string;
          status: string;
          remarks: string;
          source: string;
        }[];
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}/screening/shareholders/batch`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SHAREHOLDERS"],
    }),
    editScreeningDirector: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        director_id: string;
        data: {
          status: string;
          remarks: string;
          source: string;
        };
      }
    >({
      query: ({ map_name, account_type, director_id, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}/screening/directors/${director_id}`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["DIRECTORS"],
    }),
    editScreeningDirectorBatch: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        data: {
          director_id: string;
          status: string;
          remarks: string;
          source: string;
        }[];
      }
    >({
      query: ({ map_name, account_type, data }) => {
        return {
          url: `/maps/${map_name}/${account_type}/screening/directors/batch`,
          method: "PATCH",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["DIRECTORS"],
    }),
    postShareholderProof: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        shareholder_id: string;
        data: {
          file: File;
          source: string;
        };
      }
    >({
      query: ({ map_name, account_type, shareholder_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("source", data.source);
        return {
          url: `/maps/${map_name}/${account_type}/screening/shareholders/${shareholder_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SHAREHOLDERS"],
    }),

    shareholderListUpload: builder.mutation<
      any,
      {
        map_name: string;
        account_type: string;
        shareholder_id: string;
        data: {
          file: File;
          source: string;
          document_type: string;
          document_category: string;
        };
      }
    >({
      query: ({ map_name, account_type, shareholder_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("source", data.source);
        formData.append("document_type", data.document_type);
        formData.append("document_category", data.document_category);
        return {
          url: `/maps/${map_name}/${account_type}/shareholders/list/${shareholder_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SHAREHOLDERS"],
    }),

    getShareholderListUpload: builder.query<
      any,
      {
        map_name: string;
        account_type: string;
        shareholder_id: string;
      }
    >({
      query: ({ map_name, account_type, shareholder_id }) => ({
        url: `/maps/${map_name}/${account_type}/shareholderslist/${shareholder_id}`, // Fixed missing '/'
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: (result, error, { shareholder_id }) =>
        result ? [{ type: "DOCUMENTS", id: shareholder_id }] : [],
    }),
    getShareholderListUploadView: builder.query<
      any,
      {
        map_name: string;
        account_type: string;
        shareholder_id: string;
        document_id: string;
      }
    >({
      query: ({ map_name, account_type, shareholder_id, document_id }) => ({
        url: `/maps/${map_name}/${account_type}/shareholders/${shareholder_id}/documents/${document_id}`, // Fixed missing '/'
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: (result, error, { shareholder_id }) =>
        result ? [{ type: "DOCUMENTS", id: shareholder_id }] : [],
    }),
    deleteDocumentshareholderList: builder.mutation<
      any,
      {
        map_name: string;
        document_id: string;
        account_type: string;
      }
    >({
      query: ({ map_name, document_id, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/documents/${document_id}`, // Fixed missing '/'
        method: "DELETE",
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, error, { document_id }) =>
        result ? [{ type: "DOCUMENTS", id: document_id }] : [],
    }),
    postDirectorProof: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        director_id: string;
        data: {
          file: File;
          source: string;
        };
      }
    >({
      query: ({ map_name, account_type, director_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("source", data.source);
        return {
          url: `/maps/${map_name}/${account_type}/screening/directors/${director_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["DIRECTORS"],
    }),
    getScreeningDocumentUrl: builder.query<
      {
        id: string;
        name: string;
        url: string;
        document_type: string;
        mime_type?: string | null;
      },
      { map_name: string; account_type: string; document_id: string }
    >({
      query: ({
        map_name,
        account_type,
        document_id,
      }: {
        map_name: string;
        account_type: string;
        document_id: string;
      }) => ({
        url: `/maps/${map_name}/${account_type}/screening/documents/${document_id}`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
    }),
    postCompanyProof: builder.mutation<
      any,
      {
        map_name: string;
        account_type: "supplier" | "buyer";
        map_metadata_id: string;
        data: {
          file: File;
          source: string;
        };
      }
    >({
      query: ({ map_name, account_type, map_metadata_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("source", data.source);
        formData.append("map_metadata_id", map_metadata_id);
        return {
          url: `/maps/${map_name}/${account_type}/screening/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SCREENING"],
    }),
    getComplianceProviders: builder.query<string[], void>({
      query: () => ({
        url: `/compliance/providers`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["PROVIDERS"],
    }),
    getComplianceScreeningTools: builder.query<string[], void>({
      query: () => ({
        url: `/compliance/screening-tools`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SCREENING_TOOLS"],
    }),
    getScreeningStatus: builder.query<
      ScreeningStatus,
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/screening-completeness`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SCREENING", "CHECKLIST"],
    }),
    getOverrideRiskScore: builder.query<
      { override_risk_score: string | null },
      { map_name: string; account_type: string }
    >({
      query: ({ map_name, account_type }) => ({
        url: `/maps/${map_name}/${account_type}/override-risk-score`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["COMPLIANCE_SCORE"],
    }),
    updateOverrideRiskScore: builder.mutation<
      void,
      {
        map_name: string;
        account_type: string;
        override_risk_score: number | null;
        override_risk_reason?: string | null;
      }
    >({
      query: ({
        map_name,
        account_type,
        override_risk_score,
        override_risk_reason,
      }) => ({
        url: `/maps/${map_name}/${account_type}/override-risk-score`,
        method: "PATCH",
        data: { override_risk_score, override_risk_reason },
      }),
      invalidatesTags: ["COMPLIANCE_SCORE"],
    }),
  }),
});

export const {
  useGetComplianceTemplatesQuery,
  useGetCreditReportQuery,
  useGetChecklistQuery,
  useGetComplianceScoringQuery,
  useEditCreditReportMutation,
  useGetScreeningQuery,
  useGetScreeningShareholdersQuery,
  useGetScreeningDirectorsQuery,
  useUploadDocumentMutation,
  useGetDocumentsQuery,
  useGetSingleDocumentQuery,
  useDeleteDocumentMutation,
  useEditScreeningCompanyMutation,
  useEditCheckListCompanyMutation,
  useGetSummariesQuery,
  useEditSummariesMutation,
  useGenerateFactSheetQuery,
  useEditScreeningCompanyBatchMutation,
  useEditScreeningShareholderMutation,
  useEditScreeningShareholderBatchMutation,
  useEditScreeningDirectorMutation,
  useEditScreeningDirectorBatchMutation,
  usePostShareholderProofMutation,
  usePostDirectorProofMutation,
  useShareholderListUploadMutation,
  useGetShareholderListUploadQuery,
  useGetShareholderListUploadViewQuery,
  useDeleteDocumentshareholderListMutation,
  usePostCompanyProofMutation,
  useGetScreeningDocumentUrlQuery,
  useGetComplianceProvidersQuery,
  useGetComplianceScreeningToolsQuery,
  useGetCreditReportDocumentsQuery,
  useAddCreditReportDocumentMutation,
  useGetEDDDocumentsQuery,
  useGetScreeningStatusQuery,
  useGetEwalletsByAccountQuery,
  useGetInternalBankAccountQuery,
  useAddEwalletByAccountMutation,
  useEditEwalletByAccountMutation,
  useGetOverrideRiskScoreQuery,
  useUpdateOverrideRiskScoreMutation,
} = compliance;
